import React, { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Calendar, AlertCircle, Download, FileText, User, Search, Pencil, Trash2, MoreHorizontal, Ban } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatDate } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-green-100 text-green-800 border-green-200',
  overdue: 'bg-red-100 text-red-800 border-red-200',
  discontinued: 'bg-gray-100 text-gray-500 border-gray-200'
};

export default function DelegationPage() {
  const { user, getAuthHeader } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [actualAssigners, setActualAssigners] = useState([]); // For "Assigned By" filter
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline: '',
    assigned_to: '',
    assigned_by: '',
    priority: 'normal'
  });
  
  // Super admins list for "Assigned By" dropdown
  const [superAdmins, setSuperAdmins] = useState([]);
  
  // Edit/Delete states
  const [editingTask, setEditingTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [discontinueConfirmOpen, setDiscontinueConfirmOpen] = useState(false);
  const [taskToDiscontinue, setTaskToDiscontinue] = useState(null);
  
  // Filter states
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterAssignedBy, setFilterAssignedBy] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOverdue, setFilterOverdue] = useState(false);
  
  // Report filter states
  const [reportDate, setReportDate] = useState(() => {
    // Default to yesterday
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [reportEmployeeId, setReportEmployeeId] = useState('all');
  const [reportAssignedById, setReportAssignedById] = useState('all');

  // Check if user can manage tasks (create/view all) - based on allowed_modules, NOT role/designation
  const canManageTasks = user?.is_owner || user?.allowed_modules?.includes('delegation');

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return employees;
    return employees.filter(emp => 
      emp.name?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
      emp.employee_code?.toLowerCase().includes(employeeSearch.toLowerCase())
    );
  }, [employees, employeeSearch]);

  // Get selected employee name for display
  const selectedEmployeeName = useMemo(() => {
    if (!formData.assigned_to) return '';
    const emp = employees.find(e => e.id === formData.assigned_to);
    return emp ? `${emp.name} (${emp.employee_code})` : '';
  }, [formData.assigned_to, employees]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterEmployee !== 'all') params.append('assigned_to', filterEmployee);
      if (filterAssignedBy !== 'all') params.append('assigned_by', filterAssignedBy);
      if (filterOverdue) params.append('overdue_only', 'true');
      
      const response = await axios.get(`${API_URL}/delegation-tasks?${params.toString()}`, { headers: getAuthHeader() });
      setTasks(response.data);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    }
  };

  const fetchEmployees = async () => {
    // Fetch Office Staff employees for Doer dropdown
    if (canManageTasks) {
      try {
        const response = await axios.get(`${API_URL}/users/assigners`, { headers: getAuthHeader() });
        setEmployees(response.data);
      } catch (error) {
        console.error('Failed to fetch assigners:', error);
        // Fallback to hr-employees if assigners endpoint fails
        try {
          const fallback = await axios.get(`${API_URL}/hr-employees`, { headers: getAuthHeader() });
          setEmployees(fallback.data);
        } catch (e) {
          console.error('Fallback also failed');
        }
      }
      
      // Fetch actual assigners for "Assigned By" filter (only users who have assigned tasks)
      try {
        const assignersResponse = await axios.get(`${API_URL}/users/actual-assigners`, { headers: getAuthHeader() });
        setActualAssigners(assignersResponse.data);
      } catch (error) {
        console.error('Failed to fetch actual assigners:', error);
      }
    }
  };

  const fetchDailyReport = async (dateOverride, employeeOverride, assignedByOverride) => {
    setReportLoading(true);
    try {
      const dateToUse = dateOverride || reportDate;
      const empToUse = employeeOverride !== undefined ? employeeOverride : reportEmployeeId;
      const assignerToUse = assignedByOverride !== undefined ? assignedByOverride : reportAssignedById;
      
      const params = new URLSearchParams();
      if (dateToUse) params.append('date', dateToUse);
      if (empToUse && empToUse !== 'all') params.append('employee_id', empToUse);
      if (assignerToUse && assignerToUse !== 'all') params.append('assigned_by_id', assignerToUse);
      
      const response = await axios.get(
        `${API_URL}/delegation-tasks/report-preview?${params.toString()}`, 
        { headers: getAuthHeader() }
      );
      
      setReportData(response.data);
      setReportOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch report');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportAsCSV = () => {
    if (!reportData) return;
    
    let csv = 'Daily Productivity Report\n';
    csv += `Report Date: ${reportData.report_label}\n\n`;
    
    // Summary
    csv += 'SUMMARY\n';
    csv += 'Task Type,Completed,Overdue\n';
    csv += `Delegation,${reportData.summary?.delegation?.completed || 0},${reportData.summary?.delegation?.overdue || 0}\n`;
    csv += `Checklist,${reportData.summary?.checklist?.completed || 0},${reportData.summary?.checklist?.overdue || 0}\n`;
    csv += `SWS,${reportData.summary?.sws?.completed || 0},${reportData.summary?.sws?.overdue || 0}\n`;
    csv += `TOTAL,${reportData.summary?.total?.completed || 0},${reportData.summary?.total?.overdue || 0}\n\n`;
    
    // Completed Tasks
    csv += '=== COMPLETED TASKS ===\n\n';
    
    // Delegation Completed
    if (reportData.delegation?.completed?.length > 0) {
      csv += 'DELEGATION TASKS (Completed)\n';
      csv += 'Title,Assigned To,Assigned By,Due Date,Completed At,Priority\n';
      reportData.delegation.completed.forEach(t => {
        csv += `"${t.title || ''}","${t.assigned_to || ''}","${t.assigned_by || ''}","${t.due_date ? formatDate(t.due_date) : ''}","${t.completed_at ? formatDate(t.completed_at) : ''}","${t.priority || ''}"\n`;
      });
      csv += '\n';
    }
    
    // Checklist Completed
    if (reportData.checklist?.completed?.length > 0) {
      csv += 'CHECKLIST TASKS (Completed)\n';
      csv += 'Item,Template,Employee,Completed At\n';
      reportData.checklist.completed.forEach(t => {
        csv += `"${t.title || ''}","${t.template_name || ''}","${t.employee || ''}","${t.completed_at ? formatDate(t.completed_at) : ''}"\n`;
      });
      csv += '\n';
    }
    
    // SWS Completed
    if (reportData.sws?.completed?.length > 0) {
      csv += 'SWS TASKS (Completed)\n';
      csv += 'Task,Project/Workflow,Employee,Completed At\n';
      reportData.sws.completed.forEach(t => {
        csv += `"${t.title || ''}","${t.project || ''}","${t.employee || ''}","${t.completed_at ? formatDate(t.completed_at) : ''}"\n`;
      });
      csv += '\n';
    }
    
    // Overdue Tasks
    csv += '=== OVERDUE TASKS ===\n\n';
    
    // Delegation Overdue
    if (reportData.delegation?.overdue?.length > 0) {
      csv += 'DELEGATION TASKS (Overdue)\n';
      csv += 'Title,Assigned To,Assigned By,Due Date,Priority\n';
      reportData.delegation.overdue.forEach(t => {
        csv += `"${t.title || ''}","${t.assigned_to || ''}","${t.assigned_by || ''}","${t.due_date ? formatDate(t.due_date) : ''}","${t.priority || ''}"\n`;
      });
      csv += '\n';
    }
    
    // Checklist Overdue
    if (reportData.checklist?.overdue?.length > 0) {
      csv += 'CHECKLIST TASKS (Overdue)\n';
      csv += 'Item,Template,Employee,Due Date\n';
      reportData.checklist.overdue.forEach(t => {
        csv += `"${t.title || ''}","${t.template_name || ''}","${t.employee || ''}","${t.due_date ? formatDate(t.due_date) : ''}"\n`;
      });
      csv += '\n';
    }
    
    // SWS Overdue
    if (reportData.sws?.overdue?.length > 0) {
      csv += 'SWS TASKS (Overdue)\n';
      csv += 'Task,Project/Workflow,Employee,Due Date\n';
      reportData.sws.overdue.forEach(t => {
        csv += `"${t.title || ''}","${t.project || ''}","${t.employee || ''}","${t.due_date ? formatDate(t.due_date) : ''}"\n`;
      });
      csv += '\n';
    }
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `productivity_report_${reportData.report_date || formatDate(new Date()).replace(/\//g, '-')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  // Fetch super admins for "Assigned By" dropdown
  const fetchSuperAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/delegation-tasks/super-admins`, { headers: getAuthHeader() });
      setSuperAdmins(response.data);
      // Pre-select current user if they can manage delegation
      if (user?.is_owner || user?.allowed_modules?.includes('delegation')) {
        const currentAdmin = response.data.find(a => a.id === user.id);
        if (currentAdmin) {
          setFormData(prev => ({ ...prev, assigned_by: currentAdmin.id }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch super admins:', error);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchEmployees();
    fetchSuperAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterEmployee, filterAssignedBy, filterOverdue]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validate doer is selected
    if (!formData.assigned_to) {
      toast.error('Please select a doer for this task');
      return;
    }
    
    // Validate assigned_by is selected
    if (!formData.assigned_by) {
      toast.error('Please select who is assigning this task');
      return;
    }
    
    try {
      // Get employee name for the task
      const selectedEmployee = employees.find(e => e.id === formData.assigned_to);
      // Get super admin name for assigned_by
      const selectedAdmin = superAdmins.find(a => a.id === formData.assigned_by);
      
      // Set due date to end of day (23:59:59)
      const dueDate = new Date(formData.deadline);
      dueDate.setHours(23, 59, 59, 0);
      
      // Send correct field names that backend expects
      await axios.post(`${API_URL}/delegation-tasks`, {
        title: formData.title.toUpperCase(),
        description: (formData.description || '').toUpperCase(),
        assigned_to_id: formData.assigned_to,
        assigned_to_name: selectedEmployee?.name || '',
        assigned_by_id: formData.assigned_by,
        assigned_by_name: selectedAdmin?.name || '',
        due_date: dueDate.toISOString(),
        priority: formData.priority
      }, { headers: getAuthHeader() });
      
      toast.success('Delegation task created successfully');
      setOpen(false);
      // Reset form but preserve assigned_by if user can manage delegation
      const defaultAssignedBy = (user?.is_owner || user?.allowed_modules?.includes('delegation')) ? formData.assigned_by : '';
      setFormData({ title: '', description: '', deadline: '', assigned_to: '', assigned_by: defaultAssignedBy, priority: 'normal' });
      setEmployeeSearch('');
      setShowEmployeeDropdown(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    }
  };

  // Check if current user can edit/delete a task
  // Users with delegation access can edit/delete all tasks, others can only edit/delete their own
  const canEditDeleteTask = (task) => {
    // Use the can_edit flag from backend if available
    if (task.can_edit !== undefined) return task.can_edit;
    // Fallback for old data without can_edit flag
    if (user?.is_owner || user?.allowed_modules?.includes('delegation')) return true;
    return task.created_by === user?.id || task.assigned_by_id === user?.id;
  };
  
  // Since filtering is done on backend, just use tasks directly
  const filteredTasks = tasks;

  // Open edit dialog
  const openEditDialog = (task) => {
    const dueDate = task.due_date || task.deadline;
    setEditingTask({
      ...task,
      title: task.title,
      description: task.description || '',
      deadline: dueDate ? dueDate.split('T')[0] : '',
      assigned_to: task.assigned_to_id || task.assigned_to,
      priority: task.priority || 'normal'
    });
    setEditOpen(true);
  };

  // Handle edit submit
  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingTask) return;
    
    try {
      const dueDate = new Date(editingTask.deadline);
      dueDate.setHours(23, 59, 59, 0);
      
      await axios.patch(`${API_URL}/delegation-tasks/${editingTask.id}`, {
        title: editingTask.title.toUpperCase(),
        description: (editingTask.description || '').toUpperCase(),
        due_date: dueDate.toISOString(),
        priority: editingTask.priority
      }, { headers: getAuthHeader() });
      
      toast.success('Task updated successfully');
      setEditOpen(false);
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!taskToDelete) return;
    
    try {
      await axios.delete(`${API_URL}/delegation-tasks/${taskToDelete.id}`, { headers: getAuthHeader() });
      toast.success('Task deleted successfully');
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const handleDiscontinue = async () => {
    if (!taskToDiscontinue) return;
    
    try {
      await axios.patch(`${API_URL}/delegation-tasks/${taskToDiscontinue.id}/discontinue`, {}, { headers: getAuthHeader() });
      toast.success('Task discontinued successfully');
      setDiscontinueConfirmOpen(false);
      setTaskToDiscontinue(null);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to discontinue task');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-muted-foreground">Manage one-time tasks with specific deadlines</p>
          </div>
          <div className="flex gap-2">
            {canManageTasks && (
              <>
                <Button variant="outline" onClick={() => fetchDailyReport()} disabled={reportLoading}>
                  <FileText className="w-4 h-4 mr-2" />
                  {reportLoading ? 'Loading...' : 'Daily Report'}
                </Button>
                <Dialog open={open} onOpenChange={(isOpen) => {
                  setOpen(isOpen);
                  if (!isOpen) setEmployeeSearch('');
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-delegation-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Task
                    </Button>
                  </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Delegation Task</DialogTitle>
                  <DialogDescription>Assign a one-time task to an Office Staff employee</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        data-testid="delegation-title"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="TASK TITLE"
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        data-testid="delegation-description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="TASK DESCRIPTION..."
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deadline">Due Date *</Label>
                      <Input
                        id="deadline"
                        data-testid="delegation-deadline"
                        type="date"
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority *</Label>
                      <Select
                        value={formData.priority}
                        onValueChange={(value) => setFormData({ ...formData, priority: value })}
                      >
                        <SelectTrigger data-testid="delegation-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="IMP">IMP (Double penalty on miss)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned_by">Assigned By *</Label>
                      <Select
                        value={formData.assigned_by}
                        onValueChange={(value) => setFormData({ ...formData, assigned_by: value })}
                      >
                        <SelectTrigger data-testid="delegation-assigned-by">
                          <SelectValue placeholder="Select who is assigning..." />
                        </SelectTrigger>
                        <SelectContent>
                          {superAdmins.map((admin) => (
                            <SelectItem key={admin.id} value={admin.id}>
                              {admin.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assign To (Office Staff) *</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                        <Input
                          placeholder="Type to search employee..."
                          value={employeeSearch || selectedEmployeeName}
                          onChange={(e) => {
                            setEmployeeSearch(e.target.value);
                            setShowEmployeeDropdown(true);
                            if (e.target.value === '') {
                              setFormData({ ...formData, assigned_to: '' });
                            }
                          }}
                          onFocus={() => setShowEmployeeDropdown(true)}
                          className="pl-9"
                          data-testid="delegation-employee-search"
                        />
                        {showEmployeeDropdown && (employeeSearch || !formData.assigned_to) && (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {filteredEmployees.length > 0 ? (
                              filteredEmployees.map((emp) => (
                                <div
                                  key={emp.id}
                                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                  onClick={() => {
                                    setFormData({ ...formData, assigned_to: emp.id });
                                    setEmployeeSearch('');
                                    setShowEmployeeDropdown(false);
                                  }}
                                >
                                  {emp.name} ({emp.employee_code})
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm text-muted-foreground">No employees found</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="delegation-submit">Create Task</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        )}
          </div>
        </div>

        {/* Daily Productivity Report Dialog */}
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>📊 Daily Productivity Report</DialogTitle>
              <DialogDescription>
                {reportData && reportData.report_label}
              </DialogDescription>
            </DialogHeader>
            
            {/* Filters */}
            <div className="flex gap-4 items-end pb-4 border-b">
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Date</Label>
                <Input 
                  type="date" 
                  value={reportDate}
                  onChange={(e) => {
                    setReportDate(e.target.value);
                    fetchDailyReport(e.target.value, reportEmployeeId, reportAssignedById);
                  }}
                  className="w-full"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Assigned By</Label>
                <Select 
                  value={reportAssignedById} 
                  onValueChange={(val) => {
                    setReportAssignedById(val);
                    fetchDailyReport(reportDate, reportEmployeeId, val);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Assigners" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assigners</SelectItem>
                    {actualAssigners.map(assigner => (
                      <SelectItem key={assigner.id} value={assigner.id}>
                        {assigner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Employee</Label>
                <Select 
                  value={reportEmployeeId} 
                  onValueChange={(val) => {
                    setReportEmployeeId(val);
                    fetchDailyReport(reportDate, val, reportAssignedById);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    {employees.map(emp => (
                      <SelectItem key={emp.user_id || emp.id} value={emp.user_id || emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {reportData && (
              <div className="space-y-6">
                {/* Summary Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Task Type</TableHead>
                          <TableHead className="text-center text-green-700">Completed</TableHead>
                          <TableHead className="text-center text-red-700">Overdue</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell className="font-medium">Delegation Tasks</TableCell>
                          <TableCell className="text-center text-green-600">{reportData.summary?.delegation?.completed || 0}</TableCell>
                          <TableCell className="text-center text-red-600">{reportData.summary?.delegation?.overdue || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">Checklist Tasks</TableCell>
                          <TableCell className="text-center text-green-600">{reportData.summary?.checklist?.completed || 0}</TableCell>
                          <TableCell className="text-center text-red-600">{reportData.summary?.checklist?.overdue || 0}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="font-medium">SWS Tasks</TableCell>
                          <TableCell className="text-center text-green-600">{reportData.summary?.sws?.completed || 0}</TableCell>
                          <TableCell className="text-center text-red-600">{reportData.summary?.sws?.overdue || 0}</TableCell>
                        </TableRow>
                        <TableRow className="bg-muted/30 font-bold">
                          <TableCell>TOTAL</TableCell>
                          <TableCell className="text-center text-green-700">{reportData.summary?.total?.completed || 0}</TableCell>
                          <TableCell className="text-center text-red-700">{reportData.summary?.total?.overdue || 0}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                {/* ===== COMPLETED TASKS ===== */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-green-700 text-lg">✅ COMPLETED TASKS</h3>
                  
                  {/* Delegation Completed */}
                  {reportData.delegation?.completed?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          Delegation Tasks ({reportData.delegation.completed.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Title</TableHead>
                              <TableHead>Assigned To</TableHead>
                              <TableHead>Assigned By</TableHead>
                              <TableHead>Due</TableHead>
                              <TableHead>Completed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.delegation.completed.map((t, idx) => (
                              <TableRow key={t.id || idx}>
                                <TableCell className="font-medium">
                                  {t.title}
                                  {t.priority === 'IMP' && <Badge className="ml-2 bg-red-100 text-red-700 text-xs">IMP</Badge>}
                                </TableCell>
                                <TableCell>{t.assigned_to}</TableCell>
                                <TableCell>{t.assigned_by || '-'}</TableCell>
                                <TableCell>{t.due_date ? formatDate(t.due_date) : '-'}</TableCell>
                                <TableCell className="text-green-600">{t.completed_at ? formatDate(t.completed_at) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Checklist Completed */}
                  {reportData.checklist?.completed?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          Checklist Tasks ({reportData.checklist.completed.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Template</TableHead>
                              <TableHead>Employee</TableHead>
                              <TableHead>Completed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.checklist.completed.map((t, idx) => (
                              <TableRow key={t.id || idx}>
                                <TableCell className="font-medium">{t.title}</TableCell>
                                <TableCell>{t.template_name}</TableCell>
                                <TableCell>{t.employee}</TableCell>
                                <TableCell className="text-green-600">{t.completed_at ? formatDate(t.completed_at) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* SWS Completed */}
                  {reportData.sws?.completed?.length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                          SWS Tasks ({reportData.sws.completed.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task</TableHead>
                              <TableHead>Project/Workflow</TableHead>
                              <TableHead>Employee</TableHead>
                              <TableHead>Completed</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.sws.completed.map((t, idx) => (
                              <TableRow key={t.id || idx}>
                                <TableCell className="font-medium">{t.title}</TableCell>
                                <TableCell>{t.project}</TableCell>
                                <TableCell>{t.employee}</TableCell>
                                <TableCell className="text-green-600">{t.completed_at ? formatDate(t.completed_at) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* No completed tasks */}
                  {(!reportData.delegation?.completed?.length && !reportData.checklist?.completed?.length && !reportData.sws?.completed?.length) && (
                    <p className="text-sm text-muted-foreground italic">No tasks completed on this date</p>
                  )}
                </div>

                {/* ===== OVERDUE TASKS ===== */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-red-700 text-lg">⚠️ OVERDUE TASKS</h3>
                  
                  {/* Delegation Overdue */}
                  {reportData.delegation?.overdue?.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          Delegation Tasks ({reportData.delegation.overdue.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Title</TableHead>
                              <TableHead>Assigned To</TableHead>
                              <TableHead>Assigned By</TableHead>
                              <TableHead>Due Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.delegation.overdue.map((t, idx) => (
                              <TableRow key={t.id || idx} className="bg-red-50/50">
                                <TableCell className="font-medium">
                                  {t.title}
                                  {t.priority === 'IMP' && <Badge className="ml-2 bg-red-100 text-red-700 text-xs">IMP</Badge>}
                                </TableCell>
                                <TableCell>{t.assigned_to}</TableCell>
                                <TableCell>{t.assigned_by || '-'}</TableCell>
                                <TableCell className="text-red-600">{t.due_date ? formatDate(t.due_date) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Checklist Overdue */}
                  {reportData.checklist?.overdue?.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          Checklist Tasks ({reportData.checklist.overdue.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Template</TableHead>
                              <TableHead>Employee</TableHead>
                              <TableHead>Due Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.checklist.overdue.map((t, idx) => (
                              <TableRow key={t.id || idx} className="bg-red-50/50">
                                <TableCell className="font-medium">{t.title}</TableCell>
                                <TableCell>{t.template_name}</TableCell>
                                <TableCell>{t.employee}</TableCell>
                                <TableCell className="text-red-600">{t.due_date ? formatDate(t.due_date) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* SWS Overdue */}
                  {reportData.sws?.overdue?.length > 0 && (
                    <Card className="border-red-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                          SWS Tasks ({reportData.sws.overdue.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Task</TableHead>
                              <TableHead>Project/Workflow</TableHead>
                              <TableHead>Employee</TableHead>
                              <TableHead>Due Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {reportData.sws.overdue.map((t, idx) => (
                              <TableRow key={t.id || idx} className="bg-red-50/50">
                                <TableCell className="font-medium">{t.title}</TableCell>
                                <TableCell>{t.project}</TableCell>
                                <TableCell>{t.employee}</TableCell>
                                <TableCell className="text-red-600">{t.due_date ? formatDate(t.due_date) : '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* No overdue tasks */}
                  {(!reportData.delegation?.overdue?.length && !reportData.checklist?.overdue?.length && !reportData.sws?.overdue?.length) && (
                    <p className="text-sm text-green-600 italic">🎉 No overdue tasks!</p>
                  )}
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setReportOpen(false)}>Close</Button>
              <Button onClick={downloadReportAsCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manager/Admin View - Table format for all tasks */}
        {canManageTasks && (
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Delegation Tasks</CardTitle>
                    <CardDescription>Tasks assigned to team members, sorted by due date</CardDescription>
                  </div>
                </div>
                {/* Filters Row */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Doer:</Label>
                    <Select value={filterEmployee} onValueChange={setFilterEmployee}>
                      <SelectTrigger className="w-44" data-testid="filter-employee-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Assigned By:</Label>
                    <Select value={filterAssignedBy} onValueChange={setFilterAssignedBy}>
                      <SelectTrigger className="w-44">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {actualAssigners.map(assigner => (
                          <SelectItem key={assigner.id} value={assigner.id}>
                            {assigner.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Status:</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="discontinued">Discontinued</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="overdue-filter"
                      checked={filterOverdue}
                      onChange={(e) => setFilterOverdue(e.target.checked)}
                      className="h-4 w-4"
                    />
                    <Label htmlFor="overdue-filter" className="text-sm text-red-600 cursor-pointer">
                      Overdue Only
                    </Label>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Doer</TableHead>
                    <TableHead>Assigned By</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks
                    .sort((a, b) => {
                      // Sort by Date first (ascending), then by Priority (critical first)
                      const dateA = new Date(a.due_date || a.deadline);
                      const dateB = new Date(b.due_date || b.deadline);
                      if (dateA.getTime() !== dateB.getTime()) {
                        return dateA - dateB;
                      }
                      // Same date - IMP tasks first (also support legacy 'critical')
                      if ((a.priority === 'IMP' || a.priority === 'critical') && b.priority !== 'IMP' && b.priority !== 'critical') return -1;
                      if ((b.priority === 'IMP' || b.priority === 'critical') && a.priority !== 'IMP' && a.priority !== 'critical') return 1;
                      return 0;
                    })
                    .map((task) => {
                      const dueDate = task.due_date || task.deadline;
                      // Only mark as overdue if due date is BEFORE today (start of day)
                      // Tasks due today should NOT be marked overdue
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isOverdue = dueDate && new Date(dueDate) < today && task.status !== 'completed' && task.status !== 'discontinued';
                      const assignedEmployee = employees.find(e => e.id === task.assigned_to_id || e.id === task.assigned_to);
                      const canEditDelete = canEditDeleteTask(task);
                      return (
                        <TableRow key={task.id} className={isOverdue ? 'bg-red-50' : ''}>
                          <TableCell>
                            <div className="font-medium">{task.title}</div>
                            {task.description && (
                              <div className="text-xs text-muted-foreground">{task.description.substring(0, 60)}...</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {assignedEmployee?.name || task.assigned_to_name || 'Unknown'}
                          </TableCell>
                          <TableCell>{task.assigned_by_name || 'Unknown'}</TableCell>
                          <TableCell className={isOverdue ? 'text-red-600 font-medium' : ''}>
                            {dueDate ? formatDate(dueDate) : 'N/A'}
                            {isOverdue && <span className="ml-1">⚠️</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant={(task.priority === 'IMP' || task.priority === 'critical') ? 'destructive' : 'secondary'}>
                              {task.priority === 'IMP' ? 'IMP' : (task.priority === 'critical' ? 'IMP' : (task.priority || 'regular'))}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={statusColors[task.status === 'discontinued' ? 'discontinued' : (isOverdue ? 'overdue' : (task.status === 'completed' ? 'completed' : 'pending'))]}>
                              {task.status === 'discontinued' ? 'Discontinued' : (isOverdue ? 'Overdue' : (task.status === 'completed' ? 'Done' : 'Pending'))}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEditDelete && task.status !== 'completed' && task.status !== 'discontinued' && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" data-testid={`task-actions-${task.id}`}>
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditDialog(task)} data-testid={`task-edit-${task.id}`}>
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-gray-600"
                                    data-testid={`task-discontinue-${task.id}`}
                                    onClick={() => {
                                      setTaskToDiscontinue(task);
                                      setDiscontinueConfirmOpen(true);
                                    }}
                                  >
                                    <Ban className="w-4 h-4 mr-2" />
                                    Discontinue
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    data-testid={`task-delete-${task.id}`}
                                    onClick={() => {
                                      setTaskToDelete(task);
                                      setDeleteConfirmOpen(true);
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              {tasks.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No delegation tasks yet. Create one using the button above.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Employee View - Redirect to My Tasks */}
        {!canManageTasks && (
          <Card>
            <CardContent className="py-12 text-center space-y-4">
              <p className="text-muted-foreground">Your tasks appear in the "My Tasks" section.</p>
              <p className="text-sm text-muted-foreground">Go to <strong>Productivity → My Tasks</strong> to view and complete your assigned tasks.</p>
            </CardContent>
          </Card>
        )}

        {/* Edit Task Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Delegation Task</DialogTitle>
              <DialogDescription>Update task details</DialogDescription>
            </DialogHeader>
            {editingTask && (
              <form onSubmit={handleEdit}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-title">Title *</Label>
                    <Input
                      id="edit-title"
                      value={editingTask.title}
                      onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                      style={{ textTransform: 'uppercase' }}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-description">Description</Label>
                    <Textarea
                      id="edit-description"
                      value={editingTask.description}
                      onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-deadline">Due Date *</Label>
                    <Input
                      id="edit-deadline"
                      type="date"
                      value={editingTask.deadline}
                      onChange={(e) => setEditingTask({ ...editingTask, deadline: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-priority">Priority</Label>
                    <Select
                      value={editingTask.priority}
                      onValueChange={(value) => setEditingTask({ ...editingTask, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="IMP">IMP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button type="submit">Save Changes</Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Task?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{taskToDelete?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Discontinue Confirmation Dialog */}
        <AlertDialog open={discontinueConfirmOpen} onOpenChange={setDiscontinueConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discontinue Task?</AlertDialogTitle>
              <AlertDialogDescription>
                This will mark "{taskToDiscontinue?.title}" as discontinued. The task will be removed from active lists but all productivity scores will be preserved. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDiscontinue} className="bg-gray-600 hover:bg-gray-700" data-testid="confirm-discontinue-btn">
                Discontinue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
